/**
 * Hybrid Logical Clock (HLC) — o relógio do motor de sync (ver docs/features/SYNC.md, ADR 0030).
 *
 * Um HLC casa o relógio físico (ms) com um contador lógico, de modo que:
 *  - dois eventos no MESMO aparelho sempre avançam (o contador quebra empates dentro do mesmo ms);
 *  - ao receber um evento remoto, o relógio "puxa" pro maior tempo já visto, preservando a ordem
 *    causal mesmo que os relógios de parede dos aparelhos divirjam.
 *
 * O timestamp é serializado como string de LARGURA FIXA e TOTALMENTE ORDENÁVEL por comparação
 * lexicográfica — então `a < b` (string) responde "a aconteceu-antes-de b". É a peça que decide,
 * no last-write-wins por campo, qual escrita vence.
 *
 * Puro e injetável: recebe `physicalNow` pra ser determinístico em teste (sem Date.now real).
 */

const ISO_LENGTH = 24; // "2026-07-03T12:00:00.000Z"
const COUNTER_HEX = 4; // 16 bits de contador lógico
const NODE_LENGTH = 16;
const MAX_COUNTER = 0xffff;

/**
 * Guard de drift (M4, auditoria jul/2026): o quanto um timestamp remoto pode adiantar o relógio
 * LOCAL num recv. Um aparelho com o relógio de parede muito errado (ex.: setado em 2030) carimba
 * HLC inflado; sem teto, o `recv` puxaria nosso relógio pro tempo dele e TODO send() nosso passaria
 * a carregar o tempo inflado — um único relógio errado CONTAGIA o espaço inteiro e, pior, o estrago
 * fica PERMANENTE (send usa max(millis, phys), então nem corrigir o relógio do outro aparelho
 * desfaz). 24h é folgado pra skew legítimo (NTP mantém em segundos) e ainda barra o runaway.
 * Não descartamos a mensagem nem mexemos no timestamp DELA (isso divergiria os aparelhos, cada um
 * clampando pelo próprio relógio) — só limitamos o quanto o remoto sequestra o NOSSO relógio.
 *
 * LIMITAÇÃO (resíduo conhecido, deliberado): o clamp barra o CONTÁGIO — nosso relógio e nossas
 * futuras escritas não ficam inflados. Mas NÃO desfaz o LWW da célula que o peer inflado JÁ
 * escreveu: o timestamp cru dele fica no oplog (applyRemoteMessages persiste sem tocar) e vence
 * até o tempo real alcançá-lo; pior, uma edição local NOSSA na MESMA célula logo após receber
 * PERDE (nasce com HLC clampado, abaixo do inflado). Dano localizado às células que ele tocou e
 * temporário >> contágio permanente e espalhado. Imunidade total exigiria tratar o timestamp
 * inflado na INGESTÃO sem divergir os aparelhos — trabalho futuro (ver ADR 0039 § adendo).
 */
const MAX_DRIFT_MS = 24 * 60 * 60 * 1000;

export interface HlcState {
    millis: number;
    counter: number;
    node: string;
}

/** Normaliza o id do aparelho para largura fixa — mantém a string do timestamp ordenável. */
export function makeNode(deviceId: string): string {
    return deviceId.padStart(NODE_LENGTH, '0').slice(-NODE_LENGTH);
}

export function pack(ts: HlcState): string {
    const iso = new Date(ts.millis).toISOString();
    const counter = ts.counter.toString(16).toUpperCase().padStart(COUNTER_HEX, '0');
    return `${iso}-${counter}-${makeNode(ts.node)}`;
}

export function unpack(packed: string): HlcState {
    const iso = packed.slice(0, ISO_LENGTH);
    const counter = parseInt(packed.slice(ISO_LENGTH + 1, ISO_LENGTH + 1 + COUNTER_HEX), 16);
    const node = packed.slice(ISO_LENGTH + 1 + COUNTER_HEX + 1);
    return { millis: new Date(iso).getTime(), counter, node };
}

/** Ordem total dos timestamps. A string empacotada já ordena por (millis, contador, nó). */
export function compare(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
}

export class Hlc {
    readonly node: string;
    private millis = 0;
    private counter = 0;
    private readonly physicalNow: () => number;

    constructor(node: string, opts?: { physicalNow?: () => number; initial?: HlcState }) {
        this.node = makeNode(node);
        this.physicalNow = opts?.physicalNow ?? (() => Date.now());
        if (opts?.initial) {
            this.millis = opts.initial.millis;
            this.counter = opts.initial.counter;
        }
    }

    state(): HlcState {
        return { millis: this.millis, counter: this.counter, node: this.node };
    }

    /** Timestamp para uma nova escrita LOCAL. Estritamente maior que o último emitido. */
    send(): string {
        const phys = this.physicalNow();
        const lastMillis = this.millis;
        this.millis = Math.max(lastMillis, phys);
        this.counter = this.millis === lastMillis ? this.counter + 1 : 0;
        this.normalize();
        return pack(this.state());
    }

    /** Integra um timestamp REMOTO recebido, avançando o relógio local (algoritmo HLC padrão). */
    recv(remotePacked: string): void {
        const remote = unpack(remotePacked);
        const phys = this.physicalNow();
        // Guard de drift (M4): teto no quanto o remoto pode adiantar nosso relógio — barra o
        // contágio de um aparelho com relógio errado (ver MAX_DRIFT_MS). O `remote.millis` cru
        // segue valendo pra ordenação/LWW da própria mensagem; aqui só protegemos NOSSO relógio.
        const remoteMillis = Math.min(remote.millis, phys + MAX_DRIFT_MS);
        const lastMillis = this.millis;
        const lastCounter = this.counter;
        this.millis = Math.max(lastMillis, remoteMillis, phys);
        if (this.millis === lastMillis && this.millis === remoteMillis) {
            this.counter = Math.max(lastCounter, remote.counter) + 1;
        } else if (this.millis === lastMillis) {
            this.counter = lastCounter + 1;
        } else if (this.millis === remoteMillis) {
            this.counter = remote.counter + 1;
        } else {
            this.counter = 0;
        }
        this.normalize();
    }

    /**
     * Mantém o contador dentro dos 16 bits transbordando o excedente para o `millis` — preserva
     * a monotonicidade sem ESTOURAR no hot path. O dreno (outbox.ts) chama send() num laço
     * síncrono por célula; um lote gigante (import grande) coletando >65k células no mesmo ms
     * antes empurrava o contador além de 0xFFFF e lançava, abortando o dreno inteiro. Agora só
     * avança o relógio uns poucos ms à frente (1ms a cada 65536 eventos), que o tempo físico
     * reabsorve. O incremento é de 1 por vez, então isto roda no máximo uma iteração por chamada.
     */
    private normalize(): void {
        while (this.counter > MAX_COUNTER) {
            this.counter -= MAX_COUNTER + 1;
            this.millis += 1;
        }
    }
}
